import { useState } from "react";
import { useLocalStorage } from "@/hooks/useLocalStorage";

interface Options<T> {
  serialize?: (value: T) => string;
  deserialize?: (value: string) => T;
}

const DEFAULT_OPTIONS: Options<any> = {
  serialize: JSON.stringify,
  deserialize: JSON.parse,
};

export function useStateWithLocalStorage<T>(namespace: string, key: string, initialValue?: T, options?: Options<T>) {
  options = { ...DEFAULT_OPTIONS, ...options };

  const { setItem, getItem, removeItem } = useLocalStorage(`${namespace}:${key}`);

  const [value, setValue] = useState<T>(() => {
    try {
      const storedValue = getItem();

      if (typeof storedValue !== "undefined" && storedValue !== null) {
        return options.deserialize!(storedValue);
      }

      return initialValue as T;
    } catch (error) {
      removeItem();
      console.error(`Error reading localStorage: ${error}`);
      return initialValue as T;
    }
  });

  const setStateAndLocalStorageValue = (newValue: T | ((prevValue: T) => T)) => {
    setValue((currentValue) => {
      const updatedValue = typeof newValue === "function" ? (newValue as (prevValue: T) => T)(currentValue) : newValue;

      try {
        const serialized = options.serialize!(updatedValue);
        setItem(serialized);
      } catch (error) {
        console.error(`Error writing localStorage value: ${error}`);
        removeItem();
      }

      return updatedValue;
    });
  };

  return [value, setStateAndLocalStorageValue] as const;
}
